import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { Alert, EmptyState, LoadingBlock, Spinner } from '../components/ui';
import { ConfirmDialog } from '../components/Modal';
import { TextField } from '../components/Field';
import { formatDateTime } from '../lib/format';
import type { Organiser } from '../lib/types';

/** Invitation outstanding: they have not signed in and set a password yet. */
function isPending(organiser: Organiser): boolean {
  return organiser.status === 'FORCE_CHANGE_PASSWORD';
}

function StatusCell({ organiser }: { organiser: Organiser }) {
  if (!organiser.enabled) return <span className="badge badge-neutral">Disabled</span>;
  if (isPending(organiser)) return <span className="badge badge-warn">Invitation sent</span>;
  if (organiser.status === 'CONFIRMED') return <span className="badge badge-ok">Active</span>;
  return <span className="badge badge-neutral">{organiser.status}</span>;
}

export function OrganisersPage() {
  const { email: myEmail } = useAuth();
  const organisers = useAsync<Organiser[]>((signal) => api.admin.listOrganisers(signal), []);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Organiser | null>(null);

  const list = organisers.data ?? [];
  const isMe = (organiser: Organiser) =>
    myEmail !== null && organiser.email.toLowerCase() === myEmail.toLowerCase();

  async function onInvite(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    const trimmed = inviteEmail.trim();
    if (!trimmed) {
      setFieldError('Enter an email address');
      return;
    }

    setInviting(true);
    setInviteError(null);
    setFieldError(undefined);
    setNotice(null);

    try {
      const result = await api.admin.inviteOrganiser(trimmed);
      organisers.setData(result.organisers);
      setInviteEmail('');
      setNotice(
        result.created
          ? `Invited ${trimmed}. Cognito has emailed them a temporary password, valid for 7 days.`
          : `${trimmed} already had an account, so they have been added to the admins group.`,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setInviteError(err.message);
        setFieldError(err.fields['email']);
      } else {
        setInviteError('Could not send the invitation. Please try again.');
      }
    } finally {
      setInviting(false);
    }
  }

  async function onResend(organiser: Organiser) {
    setBusyUser(organiser.username);
    setInviteError(null);
    setNotice(null);
    try {
      await api.admin.resendOrganiserInvite(organiser.username);
      setNotice(`Invitation re-sent to ${organiser.email}.`);
    } catch (err) {
      setInviteError(
        err instanceof ApiError ? err.message : 'Could not resend that invitation.',
      );
    } finally {
      setBusyUser(null);
    }
  }

  async function onRevoke(organiser: Organiser) {
    setBusyUser(organiser.username);
    setInviteError(null);
    setNotice(null);
    try {
      const remaining = await api.admin.revokeOrganiser(organiser.username);
      organisers.setData(remaining);
      setNotice(`${organiser.email} no longer has access and has been signed out.`);
    } catch (err) {
      setInviteError(
        err instanceof ApiError ? err.message : 'Could not remove that organiser.',
      );
    } finally {
      setBusyUser(null);
      setConfirming(null);
    }
  }

  if (organisers.loading && organisers.data === null) {
    return <LoadingBlock message="Loading organisers..." />;
  }
  if (organisers.error && organisers.data === null) {
    return (
      <Alert variant="error" title="Could not load organisers">
        <p>{organisers.error}</p>
      </Alert>
    );
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>Organisers</h1>
          <p>
            Everyone who can sign in to this portal. Access is granted by membership of the{' '}
            <code>admins</code> group, so an account on its own is not enough.
          </p>
        </div>
      </div>

      {notice && (
        <Alert variant="success">
          <p style={{ marginBottom: 0 }}>{notice}</p>
        </Alert>
      )}
      {inviteError && (
        <Alert variant="error" title="Something went wrong">
          <p style={{ marginBottom: 0 }}>{inviteError}</p>
        </Alert>
      )}

      <section className="card stack">
        <h2>Invite an organiser</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          They will be emailed a temporary password and asked to set their own on first sign-in.
          Anyone you invite can invite others and remove organisers, so treat it as full access.
        </p>
        <form className="row-form" onSubmit={onInvite} noValidate>
          <TextField
            label="Email address"
            type="email"
            autoComplete="off"
            value={inviteEmail}
            error={fieldError}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              setFieldError(undefined);
            }}
          />
          <button type="submit" className="btn btn-primary" disabled={inviting}>
            {inviting ? (
              <>
                <Spinner label="Inviting" /> Inviting...
              </>
            ) : (
              'Send invitation'
            )}
          </button>
        </form>
      </section>

      <section className="card stack">
        <h2>
          Current organisers <span className="faint">({list.length})</span>
        </h2>

        {list.length === 0 ? (
          <EmptyState title="No organisers found">
            <p>
              That should not be possible while you are signed in. Try reloading; if it persists,
              check the <code>admins</code> group in Cognito.
            </p>
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                Organisers with access to the admin portal
              </caption>
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Status</th>
                  <th scope="col">Two-factor</th>
                  <th scope="col">Added</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((organiser) => {
                  const busy = busyUser === organiser.username;
                  const me = isMe(organiser);
                  return (
                    <tr key={organiser.username}>
                      <td>
                        {organiser.email}
                        {me && <span className="badge badge-info" style={{ marginLeft: '0.5rem' }}>You</span>}
                      </td>
                      <td>
                        <StatusCell organiser={organiser} />
                      </td>
                      <td>
                        {organiser.mfaEnabled === null ? (
                          <span className="faint">Unknown</span>
                        ) : organiser.mfaEnabled ? (
                          <span className="badge badge-ok">On</span>
                        ) : (
                          <span className="badge badge-warn">Off</span>
                        )}
                      </td>
                      <td>{organiser.createdAt ? formatDateTime(organiser.createdAt) : '-'}</td>
                      <td>
                        <div className="row">
                          {isPending(organiser) && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => void onResend(organiser)}
                              disabled={busy}
                            >
                              Resend invite
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setConfirming(organiser)}
                            disabled={busy || me || list.length <= 1}
                            title={
                              me
                                ? 'You cannot remove your own access'
                                : list.length <= 1
                                  ? 'Invite a replacement before removing the last organiser'
                                  : undefined
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="faint" style={{ marginBottom: 0 }}>
          Removing an organiser takes their access away immediately and signs them out of every
          device. Their account stays in Cognito, so you can restore access by inviting them again.
        </p>
      </section>

      {confirming && (
        <ConfirmDialog
          title="Remove organiser access"
          danger
          confirmLabel="Remove access"
          busy={busyUser === confirming.username}
          message={
            <>
              <p>
                <strong>{confirming.email}</strong> will lose access to the admin portal and be
                signed out everywhere.
              </p>
              <p style={{ marginBottom: 0 }}>
                Their Cognito account is kept, so inviting them again restores access.
              </p>
            </>
          }
          onConfirm={() => void onRevoke(confirming)}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
}
